import { faQuestionCircle } from '@fortawesome/free-regular-svg-icons';
import React, { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';

import { ConditionalRendering } from '@components/common/conditional-rendering';
import { CurrencyInput, ValidationErrorMessage } from '@components/common/form';
import { INPUT_TYPES } from '@components/common/form/constants/fieldTypes';
import Tooltip from '@components/common/tooltip/Tooltip';
import { PlAN_LEVEL } from '@constants/enrollmentList/enrollmentList.constant';
import { usePermissions } from '@hooks/usePermissions';
import useValidateAPTCAmount from '@hooks/useValidateAPTCAmount';
import { currencyChange } from '../../common/enrollmentUtils';
import { IEnrollmentPremiumDto } from '../../common/types';
import { EnrollmentContext } from '../../context/MemberMyEnrollmentProvider';
import { StyledField } from '../../styles/EnrollmentSharedStyled';

const ColumnElectedAPTC = ({ premium }: { premium: IEnrollmentPremiumDto }) => {
    const intl = useIntl();
    const { isEnrollmentPendingOrPendCancelled } = usePermissions();
    const validateAptc = useValidateAPTCAmount();
    const permissions = useSelector((state: any) => state.memberEnrollment?.permissions);
    const { userPermissions } = permissions || {};
    const { memberEnrlmntAptcEditable } = userPermissions;
    const { viewEditableFields } = usePermissions();

    const { enrollmentPremiumFormContext, selectedEnrollment } = useContext(EnrollmentContext);
    const {
        watch,
        setValue,
        formState: { errors },
    } = enrollmentPremiumFormContext;
    const values = watch();

    const isdisabled = useMemo(() => {
        return (
            !premium.isFinancial ||
            selectedEnrollment.planLevel === PlAN_LEVEL.CATASTROPHIC ||
            (!selectedEnrollment.isStateSubsidyEnabled && !premium.isFinancial) ||
            (selectedEnrollment.isStateSubsidyEnabled && !premium.isAptcEligible) ||
            (premium.isFinancial && !premium.isAptcEligible) ||
            !memberEnrlmntAptcEditable ||
            isEnrollmentPendingOrPendCancelled(selectedEnrollment)
        );
    }, [premium]);

    const onAptcChange = e => {
        validateAptc({ ...premium, aptcAmount: Number(parseFloat(e.target.value).toFixed(2)) });
        currencyChange(setValue, 'aptcAmount', premium, e.target.value);
    };

    const convertToDollarValue = e => {
        const rawValue = e.target.value;

        const endsInDotOrZero = rawValue.endsWith('.') || rawValue.endsWith('.0') || rawValue.endsWith('.00');

        if (endsInDotOrZero) {
            const parsed = parseFloat(rawValue);
            if (!isNaN(parsed)) {
                const formatted = parsed.toFixed(2);
                currencyChange(setValue, 'aptcAmount', premium, formatted);
            }
        }
    };

    return (
        <ConditionalRendering isTrue={viewEditableFields(userPermissions, values, premium)}>
            <StyledField>
                <CurrencyInput
                    // @ts-ignore
                    label={{
                        wrapperClasses: ['currency-input'],
                        labelClasses: [''],
                        labelText: '',
                        showRequired: false,
                        isInputRequired: false,
                    }}
                    input={{
                        id: 'aptcAmount',
                        isInputRequired: true,
                        type: INPUT_TYPES.TEXT,
                        value: premium.aptcAmount,
                        disabled: isdisabled,
                        dataTestId: 'incomeValue',
                        name: 'incomeValue',
                        placeholder: '',
                    }}
                    actions={{
                        handleChange: e => onAptcChange(e),
                        handleBlur: e => convertToDollarValue(e),
                    }}
                    maskOptions={{
                        allowDecimal: true,
                        prefix: '',
                        decimalLimit: '2',
                        integerLimit: 10,
                        includeThousandsSeparator: false,
                        allowLeadingZeroes: true,
                    }}
                    showIcon={true}
                />
                <ConditionalRendering isTrue={!premium.isFinancial}>
                    {!premium.isFinancial && (
                        <Tooltip
                            index={`aptcAmountTooltip-${premium.month}`}
                            id="aptcAmountTooltip"
                            elementId="aptcAmountTooltip"
                            content="member.enrollment_list.premiumhistory.tooltip.aptc"
                            place="top"
                            icon={faQuestionCircle}
                            dataAutomationId="aptcAmountTooltip"
                        />
                    )}
                </ConditionalRendering>
            </StyledField>
            <ConditionalRendering isTrue={premium.isFinancial}>
                <ValidationErrorMessage
                    isVisible={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.aptcAmount}
                    messageText={errors?.enrollmentPremiumDtoList?.[premium.month - 1]?.aptcAmount?.message}
                />
            </ConditionalRendering>
        </ConditionalRendering>
    );
};

export default ColumnElectedAPTC;
